import { useState, useCallback } from "react"

import {
  PredicateFilterState,
  StringPredicateFilterState,
  NumberPredicateFilterState,
  updateStringFilterState,
  updateNumberFilterState,
} from "./types/predicateFilter.types"

type StringFiltersState = Record<string, StringPredicateFilterState>
type NumberFiltersState = Record<string, NumberPredicateFilterState>

export function usePredicateFilters() {
  const [stringFilters, setStringFilters] = useState<StringFiltersState>({})
  const [numberFilters, setNumberFilters] = useState<NumberFiltersState>({})

  const setStringFilter = (filter: StringPredicateFilterState) => {
    setStringFilters((prev) => ({ ...prev, [filter.filterName]: filter }))
    setNumberFilters((prev) => {
      const { [filter.filterName]: _, ...rest } = prev
      return rest
    })
  }

  const setNumberFilter = (filter: NumberPredicateFilterState) => {
    setNumberFilters((prev) => ({ ...prev, [filter.filterName]: filter }))
    setStringFilters((prev) => {
      const { [filter.filterName]: _, ...rest } = prev
      return rest
    })
  }

  // Sets multiple filters at once, ensuring filter names remain unique across both maps.
  // If a filter name exists in the opposite type's map, it will be removed before adding.
  const setPredicateFilters = (
    filters: PredicateFilterState | PredicateFilterState[]
  ) => {
    const filterList = Array.isArray(filters) ? filters : [filters]

    const newStringFilters: StringFiltersState = {}
    const newNumberFilters: NumberFiltersState = {}
    const stringFilterNames: string[] = []
    const numberFilterNames: string[] = []

    filterList.forEach((filter) => {
      switch (filter.type) {
        case "string":
          newStringFilters[filter.filterName] = filter
          stringFilterNames.push(filter.filterName)
          break
        case "number":
          newNumberFilters[filter.filterName] = filter
          numberFilterNames.push(filter.filterName)
          break
        default:
          break
      }
    })

    setStringFilters((prev) => {
      const filtered = { ...prev }
      numberFilterNames.forEach((name) => delete filtered[name])
      return { ...filtered, ...newStringFilters }
    })

    setNumberFilters((prev) => {
      const filtered = { ...prev }
      stringFilterNames.forEach((name) => delete filtered[name])
      return { ...filtered, ...newNumberFilters }
    })
  }

  const getFilter = useCallback(
    (filterName: string): PredicateFilterState | undefined =>
      stringFilters[filterName] ?? numberFilters[filterName],
    [stringFilters, numberFilters]
  )

  const getStringFilter = useCallback(
    (filterName: string): StringPredicateFilterState | undefined =>
      stringFilters[filterName],
    [stringFilters]
  )

  const getNumberFilter = useCallback(
    (filterName: string): NumberPredicateFilterState | undefined =>
      numberFilters[filterName],
    [numberFilters]
  )

  const updateStringFilter = useCallback(
    (
      filterName: string,
      partialUpdate: Partial<StringPredicateFilterState>
    ): void => {
      setStringFilters((prevState) => {
        const existingFilter = prevState[filterName]
        if (!existingFilter) {
          return prevState
        }

        const updatedFilter = updateStringFilterState(
          existingFilter,
          partialUpdate
        )
        return { ...prevState, [filterName]: updatedFilter }
      })
    },
    [setStringFilters]
  )

  const updateNumberFilter = useCallback(
    (
      filterName: string,
      partialUpdate: Partial<NumberPredicateFilterState>
    ): void => {
      setNumberFilters((prevState) => {
        const existingFilter = prevState[filterName]
        if (!existingFilter) {
          return prevState
        }

        const updatedFilter = updateNumberFilterState(
          existingFilter,
          partialUpdate
        )
        return { ...prevState, [filterName]: updatedFilter }
      })
    },
    [setNumberFilters]
  )

  return {
    setPredicateFilters,
    setStringFilter,
    setNumberFilter,
    getFilter,
    getStringFilter,
    getNumberFilter,
    updateStringFilter,
    updateNumberFilter,
  }
}
